import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, CircleDashed, Users, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MissionCard } from "@/components/MissionCard";

function PersonCard({ u, followed, pending, onFollow }) {
  return (
    <div className="brutal-card bg-card p-4 flex items-center gap-3" data-testid={`discover-user-${u.username}`}>
      <Link to={`/profile/${u.id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80">
        <div className="w-11 h-11 rounded-full border-2 border-foreground overflow-hidden bg-accent flex items-center justify-center font-bold shrink-0">
          {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : (u.display_name || u.username || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{u.display_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            @{u.username}
            {u.mutual_count > 0 && ` · ${u.mutual_count} mutual${u.mutual_count === 1 ? "" : "s"}`}
          </div>
        </div>
      </Link>
      <button disabled={pending || followed} onClick={() => onFollow(u.id)} data-testid={`discover-follow-${u.username}`}
        className={`brutal-btn px-3 py-1.5 text-sm ${followed ? "bg-accent" : "bg-background"}`}>
        {pending ? "Following…" : followed ? "Following" : "Follow"}
      </button>
    </div>
  );
}

function CommunityCard({ c, joined, pending, onJoin }) {
  return (
    <div className="brutal-card bg-card p-4 flex items-center gap-3" data-testid={`discover-community-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{c.name}</div>
        <div className="text-xs text-muted-foreground truncate">{c.description || `${c.member_count} member${c.member_count === 1 ? "" : "s"}`}</div>
      </div>
      <button disabled={pending || joined} onClick={() => onJoin(c.id)} data-testid={`discover-join-${c.id}`}
        className={`brutal-btn px-3 py-1.5 text-sm ${joined ? "bg-accent" : "bg-background"}`}>
        {pending ? "Joining…" : joined ? "Joined" : "Join"}
      </button>
    </div>
  );
}

export default function DiscoverPage() {
  const { user } = useAuth();
  if (!user?.id) return null;
  return <AccountDiscovery key={user.id} />;
}

function AccountDiscovery() {
  const [people, setPeople] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [followed, setFollowed] = useState({});
  const [joined, setJoined] = useState({});
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const mounted = useRef(false);
  const missionVersion = useRef(0);
  const pendingActions = useRef(new Set());
  const completedActions = useRef(new Set());
  const [pending, setPending] = useState([]);

  const loadMission = useCallback(() => {
    const version = ++missionVersion.current;
    api.get("/missions/today", { timeout: 30000 }).then((response) => {
      if (mounted.current && version === missionVersion.current) setMission(response.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    mounted.current = true;
    let current = true;
    Promise.all([
      api.get("/discovery/suggested-users?limit=12", { timeout: 30000 }),
      api.get("/discovery/suggested-communities?limit=6", { timeout: 30000 }),
    ]).then(([users, groups]) => {
      if (!current) return;
      setPeople(users.data || []);
      setCommunities(groups.data || []);
    }).catch(() => { if (current) setLoadError(true); })
      .finally(() => { if (current) setLoading(false); });
    loadMission();
    return () => { current = false; mounted.current = false; missionVersion.current += 1; };
  }, [loadMission]);

  const performAction = async (kind, id) => {
    const key = `${kind}:${id}`;
    if (pendingActions.current.has(key) || completedActions.current.has(key)) return;
    pendingActions.current.add(key);
    setPending([...pendingActions.current]);
    const isFollow = kind === "follow";
    try {
      const response = await api.post(isFollow ? `/users/${id}/follow` : `/communities/${id}/join`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      const confirmed = response.data?.[isFollow ? "following" : "joined"];
      if (typeof confirmed !== "boolean") throw new Error("Missing relationship state");
      if (confirmed) completedActions.current.add(key);
      const update = isFollow ? setFollowed : setJoined;
      update((previous) => ({ ...previous, [id]: confirmed }));
      loadMission();
    } catch {
      if (mounted.current) toast.error(isFollow ? "Could not follow" : "Could not join");
    } finally {
      pendingActions.current.delete(key);
      if (mounted.current) setPending([...pendingActions.current]);
    }
  };
  const follow = (id) => performAction("follow", id);
  const join = (id) => performAction("join", id);

  return (
    <div data-testid="discover-page">
      <div className="mb-6">
        <h1 className="text-4xl flex items-center gap-2"><Compass size={32} strokeWidth={2.5} /> Discover</h1>
        <p className="text-muted-foreground mt-1">Gentle corners of HAEVEN picked for you — people and communities worth knowing.</p>
      </div>

      {mission && (
        <MissionCard
          icon={CircleDashed}
          title="Grow Your Circle"
          subtitle="Follow 2 new people and join 1 community today to complete your circle."
          tasks={[
            { label: "New follows", count: mission.circle.follows, goal: mission.circle.follows_goal },
            { label: "Communities joined", count: mission.circle.joins, goal: mission.circle.joins_goal },
          ]}
          done={mission.circle.done}
          doneLabel="Circle complete — your HAEVEN just got warmer."
          testId="circle-mission"
        />
      )}

      {loading ? (
        <div className="brutal-card p-6 bg-card">Finding your people…</div>
      ) : loadError ? (
        <div role="alert" className="brutal-card p-6 bg-card">Could not load suggestions. Please reload to try again.</div>
      ) : (
        <>
          <h2 className="text-lg font-display mb-3 flex items-center gap-2"><Users size={18} strokeWidth={2.5} /> People you may like</h2>
          {people.length === 0 ? (
            <div className="brutal-card p-5 bg-card text-sm text-muted-foreground mb-8">No suggestions right now — check back as more people join.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="discover-people-grid">
              {people.map((user) => <PersonCard key={user.id} u={user} followed={!!followed[user.id]} pending={pending.includes(`follow:${user.id}`)} onFollow={follow} />)}
            </div>
          )}

          <h2 className="text-lg font-display mb-3 flex items-center gap-2"><Sparkles size={18} strokeWidth={2.5} /> Communities to join</h2>
          {communities.length === 0 ? (
            <div className="brutal-card p-5 bg-card text-sm text-muted-foreground">You&apos;re in every community already — impressive.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="discover-communities-grid">
              {communities.map((community) => <CommunityCard key={community.id} c={community} joined={!!joined[community.id]} pending={pending.includes(`join:${community.id}`)} onJoin={join} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
